// Uma linha da EAP na tela de Desembolso: identidade do nó + a distribuição mensal do
// desembolso Ampli no período selecionado + a ação de editar a regra daquele nó.
//
// Mesma moldura visual das linhas da Projeção Financeira (`projectionTable/TableItem`):
// as classes `item`/`accordion-item-body`/`accordion-sticky-header`/`month-divider`/
// `table-date`/`accordion-content` são o padrão de layout da Ampli para esta grade, e o
// recuo e o cinza por nível seguem os mesmos valores — o cliente navega as duas telas
// como se fossem a mesma tabela.
//
// A diferença deliberada: UMA métrica por mês (o desembolso), não as dez colunas
// configuráveis da projeção. Esta tela existe para cadastrar a forma de pagar, então a
// grade mostra o que a regra produz e nada mais.
import { useEffect, useRef, useState } from "react";

import { useProjectContext } from "@contexts/projects";
import EapTreeRow from "@components/dashboard/eap/EapTreeRow";
import { formatNumberToLocale } from "@utils/formatNumber";

import { ruleKeys } from "../engine";
import { ruleText } from "../ruleText";
import { nodeLevelAcceptsRule } from "../RuleTargetPicker";
import SliceAdjustForm from "../SliceAdjustForm";
import { monthsText, reais } from "../monthEdit";
import { contributionOf, monthlyAvailability } from "../slice";
import { EDITOR_MODES } from "../editorModes";
import { useEapTable } from "./context";
import {
  inheritedRuleOf,
  ownSchedulesBelow,
  ownSchedulesText,
  planningToolText,
  scheduleMarkOf,
  scheduleMarkText,
} from "./scheduleMark";

/** `[{ month, year, value }]` → valor do mês `"MM/AAAA"`, ou `null` se o nó não tem. */
const monthlyValue = (item, date) => {
  const series = item?.monthlyProjection?.ampliDisbursement;
  if (!Array.isArray(series) || series.length === 0) return null;
  const found = series.find(
    (point) => `${String(point.month).padStart(2, "0")}/${point.year}` === date,
  );
  return found ? found.value : null;
};

/**
 * A marca "sem cronograma" (change 00, D5) e o painel curto que ela abre: o valor sem
 * cronograma, "Definir regra pelo orçamento" e o que corrigir na ferramenta de planejamento.
 */
const ScheduleMark = ({ mark, label, originId, onDefineRule }) => {
  const [open, setOpen] = useState(false);
  const container = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event) => {
      if (!container.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);
  const text = scheduleMarkText(mark);
  return (
    <span className="ampli-schedule-mark" ref={container}>
      <button
        type="button"
        className={`ampli-schedule-mark__trigger is-${mark.status}`}
        aria-expanded={open}
        aria-label={`${text}: ${label}. Ver o que fazer`}
        data-testid="schedule-mark"
        onClick={() => setOpen((value) => !value)}
      >
        <i
          className="icon icon-width-12 icon-calendar-exclamation"
          aria-hidden="true"
        />
        {text}
      </button>
      {open ? (
        <section
          className="ampli-schedule-mark__panel"
          role="dialog"
          aria-label={`Sem cronograma: ${label}`}
        >
          <strong>{`${reais(mark.unscheduledCents)} sem cronograma`}</strong>
          <p>
            {mark.status === "missing"
              ? "O orçamento deste item não tem nenhum mês na origem e nenhuma regra o agenda."
              : `${mark.unscheduledServices.toLocaleString("pt-BR")} ${
                  mark.unscheduledServices === 1
                    ? "serviço abaixo deste item não tem"
                    : "serviços abaixo deste item não têm"
                } nenhum mês na origem.`}
          </p>
          {onDefineRule ? (
            <button
              type="button"
              className="button button-accent small"
              onClick={() => {
                setOpen(false);
                onDefineRule();
              }}
            >
              Definir regra pelo orçamento
            </button>
          ) : null}
          <p className="ampli-schedule-mark__tool">
            {planningToolText(originId)}
          </p>
        </section>
      ) : null}
    </span>
  );
};

const EapRuleRow = ({
  item,
  type,
  typeTitle,
  months,
  ChildAccordionComponent,
  // Na lista plana da busca (D7): o caminho no lugar do código, o estado vindo do servidor
  // quando a linha não tem regra própria (herdada), informações extras e nenhum recorte por
  // mês — o resultado da busca não traz a série mensal do nó.
  eyebrow = null,
  stateLabel = null,
  details = null,
  flat = false,
}) => {
  const [open, setOpen] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const {
    readOnly,
    rulesByNodeKey,
    onEditRule,
    selectedMonths,
    onSliceAdjust,
    followsLabel,
    originName,
    adjustmentsByNode,
    onEditMonths,
    originId,
    originLabel,
    wbsLabels,
  } = useEapTable();
  // Contexto lido UMA vez no corpo do componente: `checkIfCubBased`/
  // `formatNumberToCurrency` leem contexto por dentro (hooks disfarçados), e chamá-los
  // uma vez por mês faria a contagem de hooks depender do tamanho do período.
  const { cubBased, activeProject } = useProjectContext();
  const indexerValue = activeProject?.originalIndexer?.value;

  const formatValue = (value) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return "—";
    if (!cubBased) return formatNumberToLocale(value);
    const basis = indexerValue && indexerValue !== 0 ? indexerValue : null;
    return basis === null ? "—" : formatNumberToLocale(value / basis);
  };

  const wbsCodeId = item?.wbs_code_id;
  const nodeKey = wbsCodeId ? ruleKeys.node(wbsCodeId) : null;
  const rule = nodeKey ? rulesByNodeKey[nodeKey] ?? null : null;
  const label = item?.description ?? item?.building_unit_name ?? wbsCodeId;
  // Os ajustes de mês do nó (recorte do gráfico; as entradas ativas, resumidas) entram no
  // rótulo da linha. A regra com base na referência (D21) nomeia a origem.
  const adjustment = wbsCodeId
    ? adjustmentsByNode?.get(wbsCodeId) ?? null
    : null;
  // O código de cada linha já desenhada, para a regra herdada nomear o ancestral (D10).
  if (wbsCodeId && item?.wbs) wbsLabels?.set(wbsCodeId, item.wbs);
  // Change 00: a regra de um nível acima que governa esta linha (D10) e a marca "sem
  // cronograma" (D5) — esta não aparece na origem "criar do zero", que já diz "sem distribuição".
  const inherited = rule ? null : inheritedRuleOf(wbsCodeId, rulesByNodeKey);
  const inheritedText = inherited
    ? (() => {
        const ancestor = String(inherited.target?.key ?? "").slice(
          "node:".length,
        );
        const name = [
          wbsLabels?.get(ancestor),
          inherited.target?.label ?? ancestor,
        ]
          .filter(Boolean)
          .join(" ");
        return `Regra herdada de ${name}: ${ruleText(inherited, originName)}`;
      })()
    : null;
  const mark = originId === "budget" ? null : scheduleMarkOf(item);
  const missingText =
    mark?.status === "missing"
      ? `Sem cronograma no ${originLabel ?? originName ?? "previsto"}`
      : null;
  const baseState = rule
    ? ruleText(rule, originName)
    : inheritedText ??
      missingText ??
      stateLabel ??
      followsLabel ??
      "à vista (padrão)";
  const ownBelow = rule
    ? ownSchedulesText(ownSchedulesBelow(wbsCodeId, rulesByNodeKey))
    : null;
  const adjusted = adjustment?.monthCount > 0;
  const stateText = adjusted
    ? `${baseState}, ajustado em ${monthsText(adjustment.monthCount)}`
    : baseState;

  // O recorte por meses (planning-workspace): sem seleção a linha é a de sempre; com ela, só
  // as linhas com valor nos meses escolhidos aparecem — e mostram a soma desses meses.
  const sliceActive = !flat && selectedMonths.size > 0;
  const monthlyPoints = item?.monthlyProjection?.ampliDisbursement;
  const sliceCents = sliceActive
    ? Math.round(contributionOf(monthlyPoints, selectedMonths) * 100)
    : null;
  if (sliceActive && !(sliceCents > 0)) return null;

  const availability = sliceActive
    ? monthlyAvailability(monthlyPoints, selectedMonths)
    : [];

  const rowEyebrow = (
    <span className="ampli-eap-row__eyebrow">
      <span className="accordion-label">
        {eyebrow ??
          `EAP #${item?.building_unit_id ?? ""}${
            item?.wbs ? `.${item.wbs}` : ""
          } / ${typeTitle}`}
      </span>
      {mark ? (
        <ScheduleMark
          mark={mark}
          label={label}
          originId={originId}
          onDefineRule={
            !readOnly && nodeKey && nodeLevelAcceptsRule(type) && onEditRule
              ? () =>
                  onEditRule(
                    { kind: "node", key: nodeKey, level: type, label },
                    rule,
                    { mode: EDITOR_MODES.schedule },
                  )
              : null
          }
        />
      ) : null}
    </span>
  );

  const rowDetails = (
    <>
      <span
        className={`ampli-eap-row__state${
          rule ? " ampli-eap-row__state--rule" : ""
        }${inheritedText ? " ampli-eap-row__state--inherited" : ""}${
          missingText && !rule && !inheritedText
            ? " ampli-eap-row__state--unscheduled"
            : ""
        }${adjusted ? " ampli-eap-row__state--adjusted" : ""}`}
      >
        {stateText}
      </span>
      {ownBelow ? (
        <span
          className="ampli-eap-row__own-below"
          data-testid="own-schedules-below"
        >
          {ownBelow}
        </span>
      ) : null}
      {details}
      {sliceActive ? (
        <span className="accordion-label" data-testid="slice-value">
          {`Meses selecionados: ${formatValue(sliceCents / 100)}`}
        </span>
      ) : null}
    </>
  );

  let editActionLabel = "Definir regra";
  if (rule) editActionLabel = "Editar regra";
  else if (adjusted) editActionLabel = "Editar ajuste";

  const rowActions = (
    <>
      {sliceActive && !readOnly && onSliceAdjust && nodeKey ? (
        <button
          type="button"
          className="button button-outline small"
          aria-expanded={adjusting}
          onClick={() => setAdjusting((value) => !value)}
        >
          Ajustar desembolso
        </button>
      ) : null}
      {!readOnly && nodeKey && nodeLevelAcceptsRule(type) ? (
        <button
          type="button"
          className="button button-outline small"
          onClick={() => {
            const target = { kind: "node", key: nodeKey, level: type, label };
            if (!rule && adjusted && onEditMonths) onEditMonths(target, rule);
            else onEditRule?.(target, rule);
          }}
        >
          {editActionLabel}
        </button>
      ) : null}
    </>
  );

  const monthCells = months.map((date) => (
    <div key={date} style={{ width: "95px", position: "relative" }}>
      <div className="month-divider" />
      <p className="table-date">{date}</p>
      <div className="accordion-content columns-1-tablet">
        <div className="data">
          <span className="label">Desembolso</span>
          <span className="value">{formatValue(monthlyValue(item, date))}</span>
        </div>
      </div>
    </div>
  ));

  return (
    <EapTreeRow
      level={type}
      label={label}
      eyebrow={rowEyebrow}
      expandable={Boolean(ChildAccordionComponent)}
      expanded={open}
      onToggle={() => setOpen((value) => !value)}
      actions={rowActions}
      details={rowDetails}
      tail={monthCells}
      flat={flat}
      rowClassName={flat ? "ampli-eap-row--flat" : ""}
      identityClassName="ampli-eap-row__identity"
      actionsClassName="ampli-eap-row__actions"
      after={
        <>
          {sliceActive && adjusting && !readOnly ? (
            <SliceAdjustForm
              label={label}
              availability={availability}
              onCancel={() => setAdjusting(false)}
              onDone={() => setAdjusting(false)}
              submitLines={(lines, toCompetence, description) =>
                onSliceAdjust(wbsCodeId, lines, toCompetence, description)
              }
            />
          ) : null}
          {open && ChildAccordionComponent ? (
            <ChildAccordionComponent parent={wbsCodeId} months={months} />
          ) : null}
        </>
      }
    />
  );
};

export default EapRuleRow;
